import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Container,
  ContentContainer,
  MobileContentContainer,
  SimpleFormContentContainer,
  ProfileContentContainer,
  VideoContainer,
  VideoWrapper,
  Video,
  SeeMoreLink
} from "./styles"
import Markdown from "../markdown"
import { validVideoUrl, truncateNearestWord } from "../../utils/stringHelpers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronUp, faChevronDown } from "@fortawesome/pro-light-svg-icons"

const Content = ({ content, videoUrl, inlineOnMobile, fullWidthVideo, simpleForm, profile, charLimit, ...rest }) => {
  const [showAllContent, setShowAllContent] = useState(charLimit && content ? content.length <= charLimit : true)

  useEffect(() => {
    if (!content) return
    if (!charLimit) {
      setShowAllContent(true)
    } else {
      setShowAllContent(content?.length <= charLimit)
    }
  }, [content])

  const truncateContent = (content) => {
    return truncateNearestWord(content, charLimit) + "..."
  }

  const seeMoreLink = (
    <SeeMoreLink onClick={() => setShowAllContent(!showAllContent)}>
      {showAllContent ? (
        <>
          <span>See less</span>
          <FontAwesomeIcon icon={faChevronUp} size="1x" />
        </>
      ) : (
        <>
          <span>See more</span>
          <FontAwesomeIcon icon={faChevronDown} size="1x" />
        </>
      )}
    </SeeMoreLink>
  )

  const contentContainer = (
    <ContentContainer inlineOnMobile={inlineOnMobile} {...rest}>
      <Markdown componentStyles={{ h1: { variant: "primary" } }}>{content}</Markdown>
    </ContentContainer>
  )

  const videoContent = () =>
    videoUrl && validVideoUrl(videoUrl) ? (
      <VideoContainer simpleForm={simpleForm} fullWidthVideo={fullWidthVideo}>
        <VideoWrapper>
          <Video
            title="Video"
            width="560"
            height="349"
            src={`${videoUrl}?color=white&rel=0`}
            frameBorder="0"
            allowFullScreen
          />
        </VideoWrapper>
      </VideoContainer>
    ) : null

  const simpleFormContentContainer = (
    <SimpleFormContentContainer fullWidthVideo={fullWidthVideo} simpleForm={simpleForm} {...rest}>
      <div>
        <Markdown
          componentStyles={{
            h1: { variant: "contentv2Heading", size: "smallv2" },
            h2: { variant: "contentv2PlainHeading", size: "smallv2" },
            h3: { variant: "contentv2PlainHeading", size: "smallv2" },
            img: { variant: "contentv2" },
            table: { variant: "contentv2" },
            details: { variant: "contentv2PlainHeading", size: "smallv2" },
            p: { variant: "contentv2", size: "smallv2" },
            li: { variant: "contentv2", size: "smallv2" }
          }}
        >
          {showAllContent ? content : truncateContent(content)}
        </Markdown>
        {charLimit && content?.length > charLimit && seeMoreLink}
      </div>
      {videoContent()}
    </SimpleFormContentContainer>
  )

  const mobileContentContainer = (
    <MobileContentContainer inlineOnMobile={inlineOnMobile}>
      <Markdown componentStyles={{ generic: { variant: "default" } }}>{content}</Markdown>
    </MobileContentContainer>
  )

  const profileContentContainer = (
    <ProfileContentContainer inlineOnMobile={inlineOnMobile} {...rest}>
      <Markdown
        componentStyles={{
          h2: { variant: "primary", size: "profile" },
          h3: { variant: "secondary", size: "profile" },
          p: { variant: "content", size: "profile" },
          li: { variant: "content", size: "profile" }
        }}
      >
        {content}
      </Markdown>
    </ProfileContentContainer>
  )

  if (content && simpleForm) {
    return (
      <Container simpleForm {...rest}>
        {simpleFormContentContainer}
      </Container>
    )
  } else if (content && profile) {
    return (
      <Container {...rest}>
        {profileContentContainer}
        {videoContent && videoContent()}
      </Container>
    )
  } else {
    return (
      <Container fullWidthVideo={fullWidthVideo} {...rest}>
        {content && contentContainer}
        {content && !!inlineOnMobile && mobileContentContainer}
        {videoContent && videoContent()}
      </Container>
    )
  }
}

Content.propTypes = {
  content: PropTypes.string,
  videoUrl: PropTypes.string,
  gridArea: PropTypes.string,
  inlineOnMobile: PropTypes.bool,
  marginless: PropTypes.bool
}

Content.defaultProps = {
  gridArea: undefined,
  inlineOnMobile: false,
  marginless: false
}

export default Content
